"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Clock, Droplets, Hammer, ClipboardCheck, Sparkles, ArrowUpRight, Check } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
const milestones = [{
  id: "phase-1",
  phase: "Phase 01",
  title: "Initial demolition & structural rough-in",
  duration: "Week 1",
  icon: Hammer,
  description: "Careful removal of dated tile, drywall, and cabinetry down to wall studs and floor joists. We inspect framing straightness, level subfloors to 1/8-inch tolerances, and reinforce joists for heavy cast-iron tubs and stone installations.",
  checklist: ["Subfloor deflection testing and sistering joists", "Containment barriers and negative air scrubbing", "Structural lumber inspection and framing squared"]
}, {
  id: "phase-2",
  phase: "Phase 02",
  title: "Plumbing relocations & drain configurations",
  duration: "Week 2",
  icon: Droplets,
  description: "Core MEP reconfiguration. We relocate drain lines, reposition supply lines for dual shower systems and freestanding tubs, and install pressure-balanced thermostatic valves with soldered copper and premium PEX-a manifolds.",
  checklist: ["Dedicated 2-inch drain line installations for curbless showers", "Solid brass rough-in valves mounted to depth gauge standards", "In-wall carrier framing for wall-hung lavatories and water closets"]
}, {
  id: "phase-3",
  phase: "Phase 03",
  title: "Continuous waterproofing systems",
  duration: "Week 2–3",
  icon: Droplets,
  description: "Application of bonded vapor-tight sheet membranes and prefabricated waterproofing boards. Every seam, inside corner, recessed shampoo niche, and bench transition is sealed with waterproof sealing tape to prevent vapor penetration.",
  checklist: ["Schluter-Kerdi bonded membrane across wet areas", "Seamless pre-sloped mortar beds with linear drains", "Zero-threshold transitions designed for curbless entry"]
}, {
  id: "phase-4",
  phase: "Phase 04",
  title: "City of Brookhaven waterproofing & plumbing inspections",
  duration: "Week 3",
  icon: ClipboardCheck,
  description: "Before enclosing any walls or setting single tiles, we conduct a continuous 24-hour shower pan water-plug test. We manage all on-site municipal building, rough plumbing, and electrical inspections directly with City of Brookhaven officials.",
  checklist: ["24-hour standing water pan flood verification", "City of Brookhaven mechanical and plumbing sign-off", "Formal photo documentation logged for homeowner records"]
}, {
  id: "phase-5",
  phase: "Phase 05",
  title: "Tile setting, custom cabinetry & final trim",
  duration: "Weeks 4–6",
  icon: Sparkles,
  description: "Precision artisan finishes. Large-format porcelain or marble tiles are laser-aligned and grouted with stain-resistant epoxy formulas. Custom white oak vanities, honed quartzite countertops, unlacquered brass trim, and frameless glass enclosures are installed.",
  checklist: ["Mitered outside tile corners and rectified grout joints", "Scribe-fit custom cabinetry and precision countertop template", "Fixture commissioning, water pressure balancing, and clean handover"]
}];
const timelinePhases = [{
  label: "Demolition & framing",
  weeks: "Week 1"
}, {
  label: "Plumbing & electrical rough-in",
  weeks: "Week 2"
}, {
  label: "Waterproofing & flood inspection",
  weeks: "Week 3"
}, {
  label: "Stone, tile & trim commissioning",
  weeks: "Weeks 4–6"
}];
export default function Planning() {
  const shouldReduceMotion = useReducedMotion();
  const containerAnimation = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 15
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="planning-your-project" data-nav="light" className="bg-background text-foreground py-20 lg:py-28 relative overflow-hidden">
      <div className="container mx-auto max-w-7xl px-5 sm:px-8 lg:px-12">
        {/* Section Header */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} variants={containerAnimation} className="max-w-3xl mb-14 lg:mb-20">
          <div className="inline-flex items-center gap-2 mb-4">
            <Badge variant="outline" className="bg-accent/60 text-accent-foreground border-border px-3 py-1 font-sans text-xs tracking-wider uppercase font-semibold rounded-md">
              Architectural planning & timelines
            </Badge>
          </div>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-foreground leading-[1.18] mb-6">
            Milestones, plumbing & waterproofing standards
          </h2>
          <p className="text-muted-foreground text-base sm:text-lg leading-relaxed font-sans max-w-2xl">
            A methodical renovation protects both your daily routine and your home equity. Here is how we coordinate structural carpentry, Brookhaven inspections, and waterproofing across a typical 4 to 6-week luxury bathroom build.
          </p>
        </motion.div>

        {/* 12-Column Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          {/* Left Column (5 columns) - Sticky Overview Atelier Glass Card */}
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.1
        }} variants={containerAnimation} className="lg:col-span-5 lg:sticky lg:top-24">
            <Card className="rounded-2xl border border-border/80 bg-card/85 backdrop-blur-md shadow-lg p-2 sm:p-4 transition-[border-color,box-shadow] duration-300 hover:border-primary/40 hover:shadow-xl">
              <CardHeader className="p-5 pb-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold uppercase tracking-widest text-primary">
                    Project schedule
                  </span>
                  <span className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                    <Clock className="w-3.5 h-3.5 text-primary" />
                    4–6 weeks total
                  </span>
                </div>
                <CardTitle className="font-serif text-2xl font-semibold text-foreground">
                  Expected project schedule
                </CardTitle>
                <CardDescription className="text-sm text-muted-foreground font-sans mt-1">
                  Our sequential schedule prevents trade overlap, maintains site cleanliness, and keeps every phase accountable.
                </CardDescription>
              </CardHeader>

              <CardContent className="p-5 pt-2">
                {/* Milestone Summary Breakdown */}
                <div className="space-y-3.5 my-4">
                  {timelinePhases.map((phase, i) => <div key={phase.label} data-index={i} className="flex items-center justify-between py-2 border-b border-border/60 text-sm font-sans">
                      <span className="text-foreground font-medium flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-primary" />
                        {phase.label}
                      </span>
                      <span className="text-muted-foreground text-xs font-mono">
                        {phase.weeks}
                      </span>
                    </div>)}
                </div>

                {/* Brookhaven Permits & Inspections */}
                <div className="mt-6 p-4 rounded-xl bg-accent/40 border border-border/70 flex items-start gap-3.5">
                  <div className="p-2 rounded-lg bg-card text-primary shrink-0 border border-border/50">
                    <ClipboardCheck className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-foreground">
                      Brookhaven permits & inspections
                    </h3>
                    <p className="text-xs text-muted-foreground leading-relaxed mt-0.5">
                      Permit applications, mechanical plan filings, and scheduled municipal inspector walkthroughs managed directly by our team.
                    </p>
                  </div>
                </div>

                {/* Accent Hairline Divider */}
                <div className="my-6">
                  <Separator className="bg-gradient-to-r from-transparent via-primary/40 to-transparent" />
                </div>
              </CardContent>

              <CardFooter className="p-5 pt-0 flex flex-col gap-3">
                <Button asChild className="w-full bg-primary text-primary-foreground font-semibold hover:bg-primary/90 transition-[transform,background-color,box-shadow] hover:-translate-y-0.5 shadow-sm rounded-lg py-5 text-sm">
                  <Link to="/ContactRequestAnEstimatePage">
                    <span>Schedule a project consultation</span>
                    <ArrowUpRight className="w-4 h-4 ml-1.5" />
                  </Link>
                </Button>
                <p className="text-center text-xs text-muted-foreground font-sans">
                  Fixed pricing, detailed timelines, and no guesswork.
                </p>
              </CardFooter>
            </Card>
          </motion.div>

          {/* Right Column (7 columns) - Accordion Milestone System */}
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.1
        }} variants={containerAnimation} className="lg:col-span-7">
            <Accordion type="single" collapsible defaultValue="phase-3" className="w-full space-y-4">
              {milestones.map((item, i) => {
              const IconComponent = item.icon;
              return <div key={item.id} data-index={i}>
                    <AccordionItem value={item.id} className="border border-border/80 rounded-xl bg-card/70 px-5 sm:px-6 transition-[border-color,background-color,box-shadow] duration-200 hover:border-primary/50 hover:bg-card shadow-2xs overflow-hidden">
                      <AccordionTrigger className="hover:no-underline py-5 text-left group">
                        <div className="flex items-start gap-4 pr-3">
                          <div className="p-2.5 rounded-lg bg-accent/70 text-foreground shrink-0 border border-border/60 group-hover:border-primary/40 group-hover:text-primary transition-colors">
                            <IconComponent className="w-5 h-5" />
                          </div>
                          <div>
                            <div className="flex items-center gap-3 mb-1">
                              <span className="text-xs font-mono font-semibold text-primary uppercase">
                                {item.phase}
                              </span>
                              <span className="text-xs text-muted-foreground font-sans bg-muted px-2 py-0.5 rounded">
                                {item.duration}
                              </span>
                            </div>
                            <h3 className="font-serif text-lg sm:text-xl font-medium text-foreground tracking-tight group-hover:text-foreground">
                              {item.title}
                            </h3>
                          </div>
                        </div>
                      </AccordionTrigger>

                      <AccordionContent className="pb-6 pt-1 text-muted-foreground font-sans text-sm leading-relaxed pl-13 sm:pl-14">
                        <p className="mb-4 text-foreground/90">{item.description}</p>

                        <div className="bg-background/80 rounded-lg p-4 border border-border/70">
                          <span className="block text-xs font-semibold uppercase tracking-wider text-foreground mb-2.5">
                            Key standards delivered
                          </span>
                          <ul className="space-y-2">
                            {item.checklist.map((point, pointIndex) => <li key={point} data-index={pointIndex} className="flex items-start gap-2.5 text-xs sm:text-sm text-muted-foreground">
                                <Check className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                                <span>{point}</span>
                              </li>)}
                          </ul>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </div>;
            })}
            </Accordion>
          </motion.div>
        </div>
      </div>
    </section>;
}